import Autoplay from 'embla-carousel-autoplay';
import { classes } from 'html-classes';
import { useMemo, useState } from 'react';

import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import type { CarouselItem as CarouselItemType } from '~/components/isolated/Carousel/interfaces';
import { IconButton } from '~/components/isolated/IconButton';
import { useDotButton } from '~/hooks/useDotButton';
import { useGlobal } from '~/hooks/useGlobal';
import { usePrevNextButtons } from '~/hooks/usePrevNextButtons';
import styles from './BannerMainCarousel.module.scss';

interface BannerMainCarouselProps {
  items: CarouselItemType[];
  slidesPerView?: number;
  className?: string;
}

const BannerMainCarousel = ({
  items,
  slidesPerView = 1,
  className,
}: BannerMainCarouselProps) => {
  const { isMobile } = useGlobal();
  const [api, setApi] = useState<CarouselApi | undefined>(undefined);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(api);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(api);

  const showArrows = items.length > slidesPerView;

  const carouselConfig = useMemo(() => {
    if (isMobile) {
      const shouldLoop = items.length > 1;
      const isOneSlide = items.length === 1;
      const duplicatedItems = items.length === 2 ? [...items, ...items] : items;

      return {
        opts: {
          loop: shouldLoop,
          align: 'center' as const,
          active: !isOneSlide,
          slidesToScroll: 1,
        },
        autoplayDelay: 6000,
        items: duplicatedItems,
        getItemKey: (item: CarouselItemType, index: number) =>
          `${item.id}-${index}`,
        itemStyle: undefined,
      };
    }

    const shouldLoop = items.length > slidesPerView;
    return {
      opts: {
        loop: shouldLoop,
        align: 'start' as const,
        active: items.length > 2,
        slidesToScroll: 1,
      },
      autoplayDelay: 5000,
      items,
      getItemKey: (item: CarouselItemType) => item.id,
      itemStyle: {
        maxWidth: slidesPerView === 1 ? '100%' : '50%',
      },
    };
  }, [isMobile, items, slidesPerView]);

  const autoplay = useMemo(
    () =>
      Autoplay({
        delay: carouselConfig.autoplayDelay,
        stopOnInteraction: false,
      }),
    [carouselConfig.autoplayDelay],
  );

  return (
    <>
      {!isMobile && showArrows && (
        <IconButton
          icon="chevron-3.1"
          iconSize={14}
          size="XL"
          variant="shadowS"
          disabled={prevBtnDisabled}
          onClick={onPrevButtonClick}
          className={styles.carouselPrevButton}
        />
      )}
      <Carousel
        opts={carouselConfig.opts}
        setApi={setApi}
        plugins={[autoplay]}
        className={classes([styles.carousel, className])}
      >
        <CarouselContent className={styles.carouselContent}>
          {carouselConfig.items.map((item, index) => (
            <CarouselItem
              key={carouselConfig.getItemKey(item, index)}
              className={styles.slide}
              style={carouselConfig.itemStyle}
            >
              {item.node}
            </CarouselItem>
          ))}
        </CarouselContent>
        {isMobile && (
          <div className={styles.carouselDots}>
            {scrollSnaps.map((_, index) => (
              <span
                key={index}
                onClick={() => onDotButtonClick(index)}
                className={classes([
                  styles.carouselDot,
                  index === selectedIndex && styles.active,
                ])}
              />
            ))}
          </div>
        )}
      </Carousel>
      {!isMobile && showArrows && (
        <IconButton
          icon="chevron-3.1"
          iconSize={14}
          size="XL"
          variant="shadowS"
          disabled={nextBtnDisabled}
          onClick={onNextButtonClick}
          className={styles.carouselNextButton}
        />
      )}
    </>
  );
};

export default BannerMainCarousel;
